import {observable, computed} from "mobx";
import {PreviousMeetingsWidgetStatisticsViewModel} from "./previous-meetings-widget.statistics.view-model";
import {PartialGameDomainModel} from "../../../domain/partial-game/models/partial-game-domain-model";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {CompetitorViewModel} from "../../../domain/common/competitor/models/competitor-view-model";
import {PREVIOUS_MEETINGS} from "../previous-meetings-widget.consts";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {PreviousMeetingsWidgetConfig} from "../previous-meetings-widget.config";
import {PreviousMeetingsWidgetPartialGameViewModel} from "./previous-meetings-widget.partial-game.view-model";
import {PreviousMeetingsWidgetUsLayoutPartialGameViewModel} from "./previous-meetings-widget.us-layout.partial-game.view-model";
import {PartialGameViewModel} from "../../../domain/partial-game/models/partial-game-view-model";
import {H2H_LAYOUT_TYPES} from "../../../core/consts";

export class PreviousMeetingsWidgetState extends BaseModuleState {
  @observable isExpanded: Boolean = false;
  @observable shouldFilterByHomeAway: Boolean = false;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, PreviousMeetingsWidgetConfig];

  @computed get gameId(): Number {
    return this.dataSource?.game.id;
  }

  @computed get collapsedLimit(): Number {
    return PREVIOUS_MEETINGS.DEFAULT_COLLAPSED_LIST_SIZE;
  }

  @computed get homeCompetitor(): CompetitorViewModel {
    return this.dataSource?.game.homeCompetitor && new CompetitorViewModel(this.dataSource?.game.homeCompetitor);
  }

  @computed get awayCompetitor(): CompetitorViewModel {
    return this.dataSource?.game.awayCompetitor && new CompetitorViewModel(this.dataSource?.game.awayCompetitor);
  }

  @computed get matches(): Array<PartialGameViewModel> {
    const models = {
      [H2H_LAYOUT_TYPES.default]: PreviousMeetingsWidgetPartialGameViewModel,
      [H2H_LAYOUT_TYPES.us]: PreviousMeetingsWidgetUsLayoutPartialGameViewModel
    };

    const previousMeetingsMatchesVM: Array<PreviousMeetingsWidgetUsLayoutPartialGameViewModel> = this.dataSource?.game?.h2hGames?.map((game: PartialGameDomainModel) => {
      if ((game.id === this.gameId) || game.id < 0 || (this.shouldFilterByHomeAway && game.homeCompetitor.id !== this.homeCompetitor.id)) {
        return null;
      }

      const date: String = this.isUsH2hLayout ? this.datetimeUtils.toShortestDate(game.startTime) : this.datetimeUtils.toShortDate(game.startTime);
      const time: String = this.datetimeUtils.toShortTime(game.startTime);
      return new models[this.dataSource?.game.h2hLayout](game, date, time);
    }).filter(match => !!match);

    return previousMeetingsMatchesVM || [];
  }

  @computed get statistics(): PreviousMeetingsWidgetStatisticsViewModel {
    const statistics = new PreviousMeetingsWidgetStatisticsViewModel();
    const matches: Array<PreviousMeetingsWidgetPartialGameViewModel> = this.matches.filter((match: PreviousMeetingsWidgetPartialGameViewModel) => match.isEnded);

    matches.forEach(match => {
      const hasDraw = !match.homeCompetitor.isWinner && !match.awayCompetitor.isWinner;

      if (hasDraw) {
        statistics.draws += 1;
      } else {
        const isCurrentHomeTeamAlsoInMatch = match.homeCompetitor.id === this.homeCompetitor.id;

        const winnerIsHomeTeam = match.homeCompetitor.isWinner;

        if (isCurrentHomeTeamAlsoInMatch) {
          winnerIsHomeTeam ? (statistics.homeWins += 1) : (statistics.awayWins += 1);
        } else {
          !winnerIsHomeTeam ? (statistics.homeWins += 1) : (statistics.awayWins += 1);
        }
      }
    });

    return statistics;
  }

  @computed get isEmptyState(): Boolean {
    return this.matches.length === 0;
  }

  @computed get isHomeAwayInverted(): Boolean {
    return  this.dataSource?.game?.isHomeAwayInverted;
  }

  @computed get isUsH2hLayout(): Boolean {
    const isUsH2hLayout: Boolean = this.dataSource?.game?.h2hLayout === H2H_LAYOUT_TYPES.us;

    return isUsH2hLayout;
  }

  get shouldRenderDraws() {
    const shouldRenderDraws = this.dataSource?.game?.sport.drawSupport;

    return shouldRenderDraws;
  }

  get emptyPreviousMeetingsMessage(): String {
    return this.terms[`H2H_EMPTY_PREVIOUS_MEETINGS_SPORTID_${this.dataSource.game.sport.id}`] || this.terms.H2H_EMPTY_PREVIOUS_MEETINGS;
  }

  @computed get showOdds(): Boolean {
    return this.appState.showOdds;
  }
}
